/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarCheck, IndianRupee, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { QueryState } from '../../components/ui/query-state.js';
import { StatCard } from '../../components/ui/stat-card.js';
import { type Client } from '../../lib/api/resources.js';
import { type Session } from '../../lib/api/sessions.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useInvoices } from '../../lib/queries/finance.js';
import { useSessions } from '../../lib/queries/sessions.js';
import { formatIst, istDayKey } from '../../lib/utils/datetime.js';
import { initialsOf } from '../../lib/utils/initials.js';
import { formatInr } from '../../lib/utils/money.js';

import { ChildSelect } from './child-select.js';

const ACTIVE = new Set(['scheduled', 'in_progress']);
const STATUS_TONE: Readonly<Record<Client['status'], BadgeTone>> = {
  active: 'green',
  inactive: 'slate',
  discharged: 'slate',
};

/** The patient header card (avatar + name + age/diagnosis/therapist + status). */
function PatientBanner({ child }: { readonly child: Client }): JSX.Element {
  const { t } = useTranslation();
  const meta = [
    child.age !== null ? t('parent.dashboardHome.years', { count: child.age }) : null,
    child.primaryDiagnosis,
    child.therapistName,
  ]
    .filter((x) => x !== null && x !== '')
    .join(' · ');
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-lg font-bold text-white">
        {initialsOf(child.fullName)}
      </span>
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-xl font-bold text-slate-800 dark:text-white">
          {child.fullName}
        </h1>
        <p className="truncate text-sm text-slate-500 dark:text-slate-400">{meta}</p>
      </div>
      <Badge label={t(`status.${child.status}`)} tone={STATUS_TONE[child.status]} />
    </div>
  );
}

function dayLabel(iso: string, t: (k: string) => string): string {
  return istDayKey(iso) === istDayKey(new Date().toISOString())
    ? t('parent.dashboardHome.today')
    : formatIst(iso, 'EEE');
}

/** Upcoming sessions list (day · type · time). */
function UpcomingList({ sessions }: { readonly sessions: readonly Session[] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <h3 className="mb-4 font-semibold text-slate-800 dark:text-white">
        {t('parent.dashboardHome.upcoming')}
      </h3>
      {sessions.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">
          {t('parent.dashboardHome.noUpcoming')}
        </p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center gap-4 py-3">
              <span className="w-12 shrink-0 text-sm font-medium text-slate-500">
                {dayLabel(s.scheduledStart, t)}
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-slate-800 dark:text-white">
                {s.sessionType ?? t('parent.dashboardHome.session')}
              </span>
              <span className="shrink-0 text-sm text-slate-500">
                {formatIst(s.scheduledStart, 'h:mm a')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Parent → Dashboard (Figma). Patient banner + 3 stat cards + upcoming sessions. */
export function ParentDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const sessions = useSessions();
  const invoices = useInvoices();
  const [childId, setChildId] = useState('');

  const children = useMemo(() => clients.data ?? [], [clients.data]);
  useEffect(() => {
    if (childId === '' && children.length > 0) {
      setChildId(children[0]?.id ?? '');
    }
  }, [children, childId]);
  const child = children.find((c) => c.id === childId);

  const upcoming = useMemo(
    () =>
      (sessions.data ?? [])
        .filter((s) => s.clientId === childId && ACTIVE.has(s.status))
        .slice()
        .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart)),
    [sessions.data, childId],
  );
  const pendingPaise = useMemo(
    () =>
      (invoices.data ?? [])
        .filter((inv) => inv.clientId === childId)
        .reduce((sum, inv) => sum + (inv.amountPaise - inv.amountPaidPaise), 0),
    [invoices.data, childId],
  );
  const next = upcoming[0];

  return (
    <div className="space-y-6 p-8">
      <ChildSelect clients={children} value={childId} onSelect={setChildId} />
      <QueryState
        isLoading={clients.isLoading}
        isError={clients.isError}
        isEmpty={children.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('parent.dashboard.empty')}
      >
        {child !== undefined && (
          <>
            <PatientBanner child={child} />
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard
                icon={CalendarCheck}
                label={t('parent.dashboardHome.nextSession')}
                value={
                  next !== undefined
                    ? dayLabel(next.scheduledStart, t)
                    : t('parent.dashboardHome.none')
                }
                accent="bg-[var(--accent)]"
                hint={
                  next !== undefined
                    ? `${formatIst(next.scheduledStart, 'h:mm a')} · ${next.sessionType ?? t('parent.dashboardHome.session')}`
                    : t('parent.dashboardHome.noUpcoming')
                }
              />
              <StatCard
                icon={TrendingUp}
                label={t('parent.dashboardHome.progress')}
                value={`${String(child.progressPct)}%`}
                accent="bg-emerald-600"
                hint={t('parent.dashboardHome.goalsOnTrack')}
              />
              <StatCard
                icon={IndianRupee}
                label={t('parent.dashboardHome.pendingDues')}
                value={formatInr(pendingPaise)}
                accent="bg-orange-500"
                hint={
                  pendingPaise <= 0
                    ? t('parent.dashboardHome.allPaid')
                    : t('parent.dashboardHome.due')
                }
              />
            </div>
            <UpcomingList sessions={upcoming.slice(0, 6)} />
          </>
        )}
      </QueryState>
    </div>
  );
}
